import React, { useEffect, useMemo, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { useHistory, useLocation } from 'react-router-dom'; // react-router v5
import 'react-toastify/dist/ReactToastify.css';
import { useUpdateFcmTokenMutation } from 'graphql/types';
import { getClientTimezone, urlBase64ToUint8Array } from 'helpers/push';
import {
    NotificationPrompt,
    PromptHeader,
    NotificationIcon,
    PromptTitle,
    PromptMessage,
    ButtonContainer,
    YesButton,
    NoButton,
} from './index.styled';

// Using shared helper from helpers/push

const Notification: React.FC = () => {
    const [updateFcmTokenMutation] = useUpdateFcmTokenMutation();
    const { pathname } = useLocation();
    const history = useHistory();

    const [showPrompt, setShowPrompt] = useState(false);

    const VAPID_PUBLIC_KEY = useMemo(
        () => process.env.REACT_APP_FIREBASE_VAPIDKEY || '',
        [],
    );

    const subscribeToPush = async () => {
        try {
            if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
                toast.error('Notifications are not supported in this browser.');
                return;
            }

            const perm = await (window.Notification?.requestPermission?.() ?? Promise.resolve('default'));
            if (perm !== 'granted') {
                toast.info('Notifications disabled. You can enable them later in browser settings.');
                // localStorage.setItem('notifPromptDismissed', '1');
                setShowPrompt(false);
                return;
            }

            await navigator.serviceWorker.register('/sw.js');
            // 🔑 IMPORTANT: wait until the SW is active
            const readyReg = await navigator.serviceWorker.ready;

            // Optional: avoid duplicate subscriptions
            let sub = await readyReg.pushManager.getSubscription();
            if (!sub) {
                const appServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
                sub = await readyReg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: appServerKey.buffer as ArrayBuffer,
                });
            }

            try {
                await updateFcmTokenMutation({
                    variables: {
                        input: {
                            fcmToken: JSON.stringify(sub),
                            flag: 1,
                            timezone: getClientTimezone(),
                        },
                    },
                });
            } catch (err) {
                toast.error('You have to login to enable notifications.');
                console.error('updateFcmToken error', err);
                return;
            }

            localStorage.setItem('notifPromptDismissed', '1');
            setShowPrompt(false);
            toast.success('Notifications enabled.');
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'An error occurred while enabling notifications.');
            // toast.error('Failed to enable notifications.');
        }
    }

    const disableNotification = async () => {
        try {
            await updateFcmTokenMutation({
                variables: {
                    input: {
                        fcmToken: '',
                        flag: 0,
                        timezone: getClientTimezone(),
                    },
                },
            });
            localStorage.setItem('notifPromptDismissed', '1');
        } catch (err) {
            toast.error('You have to login to enable notifications.');
            console.error('updateFcmToken error', err);
            return;
        }

        setShowPrompt(false);
    }

    useEffect(() => {
        const shouldPrompt = pathname === '/' && typeof window !== 'undefined' && !!window.Notification &&
            // window.Notification.permission !== 'granted' &&
            localStorage.getItem('notifPromptDismissed') !== '1';

        setShowPrompt(shouldPrompt);
    }, [pathname]);

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        const onMessage = (event: MessageEvent) => {
            console.log("Service worker message received");
            const data = (event as MessageEvent<any>).data;
            if (!data || data.type !== 'SW_NAVIGATE' || typeof data.url !== 'string') return;

            try {
                const url = new URL(data.url, window.location.origin);
                if (url.origin !== window.location.origin) return;
                const next = `${url.pathname}${url.search}${url.hash}`;
                if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
                    history.push(next);
                }
            } catch {
                // ignore invalid URLs
            }
        };

        navigator.serviceWorker.addEventListener('message', onMessage);
        return () => navigator.serviceWorker.removeEventListener('message', onMessage);
    }, [history]);

    return (
        <>
            {showPrompt && (
                <NotificationPrompt>
                    <PromptHeader>
                        <NotificationIcon role="img" aria-label="notifications">
                            <span role="img" aria-label="notify image">🔔</span>
                        </NotificationIcon>
                        <PromptTitle>Stay Updated</PromptTitle>
                    </PromptHeader>
                    <PromptMessage>
                        Enable notifications to receive real-time chat messages while you're away
                    </PromptMessage>
                    <ButtonContainer>
                        <YesButton
                            onClick={async () => {
                                try {
                                    await subscribeToPush();
                                } catch (e) {
                                    console.error('Failed to subscribe to push notifications', e);
                                }
                            }}
                        >
                            Yes
                        </YesButton>
                        <NoButton
                            onClick={async () => {
                                await disableNotification();
                            }}
                        >
                            No
                        </NoButton>
                    </ButtonContainer>
                </NotificationPrompt>
            )}
            <ToastContainer />
        </>
    );
};

export default Notification;
